// ─── api.js ───────────────────────────────────────────────────────────────────
//  Serviço centralizado de comunicação com o backend
//  Coloque este arquivo em: src/services/api.js
// ─────────────────────────────────────────────────────────────────────────────
import { jwtDecode } from "jwt-decode";
export const API_BASE_URL = process.env.REACT_APP_API_URL;

// ─── Utilitário base ──────────────────────────────────────────────────────────

async function apiFetch(path, options = {}) {
  const token = localStorage.getItem('token');
  const isFormData = options.body instanceof FormData;

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  // Token expirado ou inválido — limpa sessão e redireciona
  if (response.status === 401 && !path.includes('/chronos/')) {
    localStorage.removeItem('token');
    window.location.href = '/';
    return;
  }

  if (!response.ok) {
    const erro = await response.json().catch(() => ({ detail: 'Erro desconhecido' }));
    const msg = typeof erro.detail === 'string' ? erro.detail : `Erro ${response.status}`;
    const err = new Error(msg);
    err.data = erro.detail;
    throw err;
  }

  return response.json();
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

export async function login(email, senha) {
  const response = await fetch(`${API_BASE_URL}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ username: email, password: senha }),
  });

  if (!response.ok) {
    const erro = await response.json().catch(() => ({}));
    throw new Error(erro.detail || 'Credenciais inválidas');
  }

  const data = await response.json();
  localStorage.setItem('token', data.access_token);
  return data;
}

export function logout() {
  localStorage.removeItem('token');
  window.location.href = '/login';
}

export async function esqueciSenha(email) {
  const response = await fetch(`${API_BASE_URL}/esqueci-senha`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.detail || 'Erro ao solicitar recuperação de senha');
  }
  return data;
}

export async function redefinirSenha(token, novaSenha) {
  const response = await fetch(`${API_BASE_URL}/redefinir-senha`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, nova_senha: novaSenha }),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.detail || 'Erro ao redefinir senha');
  }
  return data;
}

export function isAuthenticated() {
  return !!localStorage.getItem('token');
}

// ─── Chronos ──────────────────────────────────────────────────────────────────

export async function chronosLogin() {
  return apiFetch('/api/chronos/login', { method: 'POST' });
}

export async function getPontosChronos(inicioMes, finalMes, usuarioId = '0') {
  const params = new URLSearchParams({ inicio_mes: inicioMes, final_mes: finalMes, usuario_id: usuarioId });
  return apiFetch(`/api/chronos/pontos?${params}`);
}

export const getRoleFromToken = () => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  try {
    const decoded = jwtDecode(token);
    return decoded.role; // O nome da claim que você definiu no Python
  } catch (error) {
    return null;
  }
};
// ─── Dados ────────────────────────────────────────────────────────────────────

export const getDadosNfe                      = () => apiFetch('/api/dados-nfe');
export const getDadosLancamentos              = () => apiFetch('/api/dados-lancamentos');
export const getDadosLancamentosDash          = () => apiFetch('/api/dados-lancamentos-dash');
export const getDadosLancamentos2025          = () => apiFetch('/api/dados-lancamentos2025');
export const getDadosCompetencia              = () => apiFetch('/api/dados-competencia');
export const getDadosSalariosNydhus           = () => apiFetch('/api/dados-salarioNydhus');
export const getDadosFolha                    = () => apiFetch('/api/dados-folha');
export const getDadosDespesas                 = () => apiFetch('/api/dados-despesas');
export const getDadosOrcamento                = () => apiFetch('/api/dados-orcamento');
export const getTodosDados                    = () => apiFetch('/api/dados');
export const getMovimentosGeral               = () => apiFetch('/api/dados-movimentos-geral')
export const getSaldosMovimento               = () => apiFetch('/api/dados-saldos-movimentos')
export const getSaldosDiarios                 = () => apiFetch('/api/dados-saldos-diarios')
export const getMaoDeObra                     = () => apiFetch('/api/dados-mao-de-obra')
export const getLancamentosRealizado          = () => apiFetch('/api/dados-relizado')
export const getDadosContratos                = () => apiFetch('/api/dados-contratos')
export const getDadosLuciano                  = () => apiFetch('/api/dados-luciano')
export const getDadosFaturamento              = () => apiFetch('/api/dados-faturamento')
export const getDadosGp                       = () => apiFetch('/api/dados-gp')
export const getDadosInadimplencia            = () => apiFetch('/api/dados-inadimplencia');
export const getDadosControleCompetencia      = () => apiFetch("/api/dados-controle-competencia")
export const getDadosBacklog2026              = () => apiFetch("/api/dados-backlog-2026")
export const getDadosBacklog2025              = () => apiFetch("/api/dados-backlog-2025")
export const getDadosFluxoCaixa               = () => apiFetch("/api/dados-fluxo-caixa")
export const getDadosReceitasFaturadas        = () => apiFetch("/api/dados-receitas-faturadas")
export const getAdminUsuariosStats            = () => apiFetch("/api/admin/usuarios-stats")
export const getMapeamentoOrigemDados         = () => apiFetch("/api/admin/mapeamento-origem-dados")
export const getOmieContratosOS                = () => apiFetch("/api/omie/contratos-os")
export const getOmieClientesStatus              = () => apiFetch("/api/omie/clientes/status")
export const postOmieClientesSincronizar        = () => apiFetch("/api/omie/clientes/sincronizar", { method: "POST" })

export const getMinhasSolicitacoes            = () => apiFetch('/api/minhas-solicitacoes')

export const postFormulario = (dados) => apiFetch('/api/enviar-formulario', {
    method: 'POST',
    body: JSON.stringify(dados),
    headers: {
        'Content-Type': 'application/json'
    }
});

export const getConfiguracaoCompetencia = () => apiFetch('/api/configuracao-competencia');
export const putConfiguracaoCompetencia = (dados) => apiFetch('/api/configuracao-competencia', { method: 'PUT', body: JSON.stringify(dados) });

export const getColaboradores                 = () => apiFetch('/colaboradores');
export const postColaborador                  = (dados) => apiFetch('/colaboradores', { method: 'POST', body: JSON.stringify(dados) });
export const putColaborador                   = (id, dados) => apiFetch(`/colaboradores/${id}`, { method: 'PUT', body: JSON.stringify(dados) });
export const deleteColaborador                = (id) => apiFetch(`/colaboradores/${id}`, { method: 'DELETE' });
export const getReajustes                     = (colaboradorId) => apiFetch(`/reajustes/${colaboradorId}`);
export const postReajuste                     = (dados) => apiFetch('/reajustes', { method: 'POST', body: JSON.stringify(dados) });
export const deleteReajuste                   = (id) => apiFetch(`/reajustes/item/${id}`, { method: 'DELETE' });
export const getSalarioMensal                 = (colaboradorId, ano) => apiFetch(`/colaboradores/${colaboradorId}/salario-mensal?ano=${ano}`);
export const getSalarioMensalTodos            = (ano) => apiFetch(`/colaboradores/salario-mensal-todos?ano=${ano}`);
export const getDespesas                      = () => apiFetch('/despesas');
export const getDre                           = (queryString = '') => apiFetch(`/dre${queryString ? `?${queryString}` : ''}`);
export const getDadosDrePlanilha              = () => apiFetch('/api/dados-dre');
export const getDadosDreOrcamentoPlanilha     = () => apiFetch('/api/dados-dre-orcamento');
export const getDadosMargemOrcada             = () => apiFetch('/api/dados-margem-orcada');
export const postDespesa                      = (dados) => apiFetch('/despesas', { method: 'POST', body: JSON.stringify(dados) });
export const putDespesa                       = (id, dados) => apiFetch(`/despesas/${id}`, { method: 'PUT', body: JSON.stringify(dados) });
export const deleteDespesa                    = (id) => apiFetch(`/despesas/${id}`, { method: 'DELETE' });

export const getNovasVendas                   = () => apiFetch('/novas-vendas');
export const getNovaVenda                     = (id) => apiFetch(`/novas-vendas/${id}`);
export const postNovaVenda                    = (dados) => apiFetch('/novas-vendas', { method: 'POST', body: JSON.stringify(dados) });
export const putNovaVenda                     = (id, dados) => apiFetch(`/novas-vendas/${id}`, { method: 'PUT', body: JSON.stringify(dados) });
export const deleteNovaVenda                  = (id) => apiFetch(`/novas-vendas/${id}`, { method: 'DELETE' });

// ─── Gestão Orçamentária · Despesas Gerais ─────────────────────────────────

export const getIndicesOrcamento                  = (ano) => apiFetch(`/orcamento/indices?ano=${ano}`);
export const putIndicesOrcamento                  = (ano, lista) => apiFetch(`/orcamento/indices/${ano}`, { method: 'PUT', body: JSON.stringify(lista) });

export const getCategoriasOrcamento               = (departamento) => apiFetch(`/orcamento/categorias${departamento ? `?departamento=${encodeURIComponent(departamento)}` : ''}`);
export const postCategoriaOrcamento                = (dados) => apiFetch('/orcamento/categorias', { method: 'POST', body: JSON.stringify(dados) });
export const putCategoriaOrcamento                 = (id, dados) => apiFetch(`/orcamento/categorias/${id}`, { method: 'PUT', body: JSON.stringify(dados) });
export const deleteCategoriaOrcamento              = (id) => apiFetch(`/orcamento/categorias/${id}`, { method: 'DELETE' });

export const getStatusCategorias                  = (ano) => apiFetch(`/orcamento/categorias/status?ano=${ano}`);
export const putStatusCategoria                   = (categoriaId, ano, concluido, projetoId) => apiFetch(`/orcamento/categorias/${categoriaId}/status?ano=${ano}${projetoId ? `&projeto_id=${projetoId}` : ''}`, { method: 'PUT', body: JSON.stringify({ concluido }) });

export const getDespesasGerais                     = (ano, departamento) => apiFetch(`/orcamento/despesas-gerais?ano=${ano}${departamento ? `&departamento=${encodeURIComponent(departamento)}` : ''}`);
export const getMaoDeObraDespesasGerais            = (ano, departamento) => apiFetch(`/orcamento/despesas-gerais/mao-de-obra?ano=${ano}&departamento=${encodeURIComponent(departamento)}`);
export const getDespesasGeraisRealizado             = (ano, departamento) => apiFetch(`/orcamento/despesas-gerais-realizado?ano=${ano}${departamento ? `&departamento=${encodeURIComponent(departamento)}` : ''}`);
export const postDespesaGeral                      = (dados) => apiFetch('/orcamento/despesas-gerais', { method: 'POST', body: JSON.stringify(dados) });
export const putDespesaGeral                       = (id, dados) => apiFetch(`/orcamento/despesas-gerais/${id}`, { method: 'PUT', body: JSON.stringify(dados) });
export const deleteDespesaGeral                    = (id) => apiFetch(`/orcamento/despesas-gerais/${id}`, { method: 'DELETE' });

// Ticket médio de Viagens travado por ANO, global (Backoffice) — compartilhado por todos os
// departamentos de Despesas Gerais, não por departamento (ver DespesaTicketViagem no backend).
export const getTicketsViagemDespesasGerais        = (ano) => apiFetch(`/orcamento/despesas-gerais/tickets-viagem?ano=${ano}`);
export const putTicketViagemDespesasGerais         = (categoriaCodigo, ticketMedio, ano) =>
  apiFetch(`/orcamento/despesas-gerais/tickets-viagem?ano=${ano}`, { method: 'PUT', body: JSON.stringify({ categoria_codigo: categoriaCodigo, ticket_medio: ticketMedio }) });

export const postGrupoViagemDespesaGeral           = (dados) => apiFetch('/orcamento/despesas-gerais/viagens', { method: 'POST', body: JSON.stringify(dados) });
export const putGrupoViagemDespesaGeral            = (itemGrupoId, dados) => apiFetch(`/orcamento/despesas-gerais/viagens/${itemGrupoId}`, { method: 'PUT', body: JSON.stringify(dados) });
export const deleteGrupoViagemDespesaGeral         = (itemGrupoId) => apiFetch(`/orcamento/despesas-gerais/viagens/${itemGrupoId}`, { method: 'DELETE' });

// "Lançar Kit Admissão" (2.7 Equipamentos e Materiais) — usa o ticket já cadastrado em
// Premissas · Equipamentos e Materiais (getEquipamentosTicketMedio), sem ticket próprio.
export const postGrupoKitAdmissaoDespesaGeral      = (dados) => apiFetch('/orcamento/despesas-gerais/kits-admissao', { method: 'POST', body: JSON.stringify(dados) });
export const putGrupoKitAdmissaoDespesaGeral       = (itemGrupoId, dados) => apiFetch(`/orcamento/despesas-gerais/kits-admissao/${itemGrupoId}`, { method: 'PUT', body: JSON.stringify(dados) });
export const deleteGrupoKitAdmissaoDespesaGeral    = (itemGrupoId) => apiFetch(`/orcamento/despesas-gerais/kits-admissao/${itemGrupoId}`, { method: 'DELETE' });

// Reconciliação Contratações (Mão de Obra) x Kits Admissão lançados, por departamento/mês —
// usada tanto na sugestão ao vivo do modal de lançamento quanto na página de auditoria.
export const getReconciliacaoKitsAdmissao          = (ano) => apiFetch(`/orcamento/kits-admissao/reconciliacao?ano=${ano}`);

// ─── Gestão Orçamentária · Premissas de Faturamento ────────────────────────

export const getPremissasFaturamento               = (ano, versao = 'Principal') => apiFetch(`/orcamento/premissas-faturamento?ano=${ano}&versao=${encodeURIComponent(versao)}`);
export const putPremissasFaturamento                = (dados) => apiFetch('/orcamento/premissas-faturamento', { method: 'PUT', body: JSON.stringify(dados) });

// ─── Gestão Orçamentária · Faturamento Bruto ───────────────────────────────

export const getFaturamentoBruto              = (ano, empresa) => apiFetch(`/faturamento-bruto?ano=${ano}${empresa ? `&empresa=${encodeURIComponent(empresa)}` : ''}`);
export const getResumoFaturamentoBruto        = (ano, empresa) => apiFetch(`/faturamento-bruto/resumo?ano=${ano}${empresa ? `&empresa=${encodeURIComponent(empresa)}` : ''}`);
export const postFaturamentoBruto             = (dados) => apiFetch('/faturamento-bruto', { method: 'POST', body: JSON.stringify(dados) });
export const putFaturamentoBruto              = (id, dados) => apiFetch(`/faturamento-bruto/${id}`, { method: 'PUT', body: JSON.stringify(dados) });
export const deleteFaturamentoBruto           = (id) => apiFetch(`/faturamento-bruto/${id}`, { method: 'DELETE' });

// ─── Gestão Orçamentária · Novas Vendas ────────────────────────────────────

export const getNovasVendasOrcamento          = (ano) => apiFetch(`/orcamento/novas-vendas?ano=${ano}`);
export const getResumoNovasVendasOrcamento    = (ano) => apiFetch(`/orcamento/novas-vendas/resumo?ano=${ano}`);
export const postNovasVendasOrcamento         = (dados) => apiFetch('/orcamento/novas-vendas', { method: 'POST', body: JSON.stringify(dados) });
export const putNovasVendasOrcamento          = (id, dados) => apiFetch(`/orcamento/novas-vendas/${id}`, { method: 'PUT', body: JSON.stringify(dados) });
export const deleteNovasVendasOrcamento       = (id) => apiFetch(`/orcamento/novas-vendas/${id}`, { method: 'DELETE' });

// ─── Gestão Orçamentária · Custo Novas Vendas ──────────────────────────────

export const getCustoNovasVendas              = (ano) => apiFetch(`/orcamento/custo-novas-vendas?ano=${ano}`);
export const putCustoNovasVendasCelula        = (dados) => apiFetch('/orcamento/custo-novas-vendas/celula', { method: 'PUT', body: JSON.stringify(dados) });

// ─── Gestão Orçamentária · Custo Mão de Obra ───────────────────────────────

export const getColaboradoresMaoDeObra        = (ano) => apiFetch(`/orcamento/mao-de-obra?ano=${ano}`);
export const getResumoMaoDeObra               = (ano) => apiFetch(`/orcamento/mao-de-obra/resumo?ano=${ano}`);
export const postColaboradorMaoDeObra         = (dados) => apiFetch('/orcamento/mao-de-obra', { method: 'POST', body: JSON.stringify(dados) });
// `ano` = ano selecionado na tela: num ano posterior ao de entrada do colaborador, a edição vale
// daquele ano em diante e o DELETE desliga (mantém o histórico) em vez de excluir.
export const putColaboradorMaoDeObra          = (id, dados, ano) => apiFetch(`/orcamento/mao-de-obra/${id}?ano=${ano}`, { method: 'PUT', body: JSON.stringify(dados) });
export const putReajusteMaoDeObra             = (id, ordem, dados, ano) => apiFetch(`/orcamento/mao-de-obra/${id}/reajuste/${ordem}?ano=${ano}`, { method: 'PUT', body: JSON.stringify(dados) });
export const putSemReajusteMaoDeObra          = (id, semReajuste, ano) => apiFetch(`/orcamento/mao-de-obra/${id}/sem-reajuste?ano=${ano}`, { method: 'PUT', body: JSON.stringify({ sem_reajuste: semReajuste }) });
export const deleteColaboradorMaoDeObra       = (id, ano) => apiFetch(`/orcamento/mao-de-obra/${id}?ano=${ano}`, { method: 'DELETE' });
export const getParametrosEncargosMaoDeObra   = (ano) => apiFetch(`/orcamento/mao-de-obra/parametros-encargos?ano=${ano}`);
// Parâmetros herdam do último ano com parâmetros próprios — o PUT leva o `ano` da tela para, num
// ano herdado, copiar o ano de origem antes de gravar (em vez de alterar o ano de origem).
export const putParametroEncargoMaoDeObra     = (id, dados, ano) => apiFetch(`/orcamento/mao-de-obra/parametros-encargos/${id}?ano=${ano}`, { method: 'PUT', body: JSON.stringify(dados) });
export const deleteParametrosEncargosMaoDeObra = (ano) => apiFetch(`/orcamento/mao-de-obra/parametros-encargos?ano=${ano}`, { method: 'DELETE' });
export const getOrigemParametrosMaoDeObra     = (ano) => apiFetch(`/orcamento/mao-de-obra/parametros/origem?ano=${ano}`);
// "Incluir no Total" é por departamento — alguns aceitam determinado encargo (ex: FGTS Multa)
// no custo total, outros não. O GET devolve o mapa efetivo {"regime:codigo": bool} já mesclado
// com o padrão global (não é preciso saber o default no frontend).
export const putIncluirNoTotalMaoDeObra       = (regime, codigo, ano, incluirNoTotal) => apiFetch(`/orcamento/mao-de-obra/parametros-encargos/incluir-no-total/${encodeURIComponent(regime)}/${encodeURIComponent(codigo)}?ano=${ano}`, { method: 'PUT', body: JSON.stringify({ incluir_no_total: incluirNoTotal }) });
export const getParametrosBeneficiosMaoDeObra = (ano) => apiFetch(`/orcamento/mao-de-obra/parametros-beneficios?ano=${ano}`);
export const putParametroBeneficioMaoDeObra   = (id, dados, ano) => apiFetch(`/orcamento/mao-de-obra/parametros-beneficios/${id}?ano=${ano}`, { method: 'PUT', body: JSON.stringify(dados) });
export const deleteParametrosBeneficiosMaoDeObra = (ano) => apiFetch(`/orcamento/mao-de-obra/parametros-beneficios?ano=${ano}`, { method: 'DELETE' });
export const getTabelaSalarialMaoDeObra       = (ano) => apiFetch(`/orcamento/mao-de-obra/tabela-salarial${ano ? `?ano=${ano}` : ''}`);
export const putTabelaSalarialMaoDeObra       = (ano, dados) => apiFetch(`/orcamento/mao-de-obra/tabela-salarial/${ano}`, { method: 'PUT', body: JSON.stringify(dados) });
export const deleteTabelaSalarialMaoDeObra    = (ano) => apiFetch(`/orcamento/mao-de-obra/tabela-salarial/${ano}`, { method: 'DELETE' });

// ─── Gestão Orçamentária · Projetos ─────────────────────────────────────────

export const getProjetos    = (apenasAtivos = false) => apiFetch(`/orcamento/projetos${apenasAtivos ? '?apenas_ativos=true' : ''}`);
export const postProjeto    = (dados) => apiFetch('/orcamento/projetos', { method: 'POST', body: JSON.stringify(dados) });
export const putProjeto     = (id, dados) => apiFetch(`/orcamento/projetos/${id}`, { method: 'PUT', body: JSON.stringify(dados) });

// ─── Gestão Orçamentária · Alocação de Pessoal ─────────────────────────────

export const getAlocacaoPessoal = (ano) => apiFetch(`/orcamento/alocacao-pessoal?ano=${ano}`);
export const putAlocacaoPessoal = (colaboradorId, mes, ano, alocacoes) =>
  apiFetch(`/orcamento/alocacao-pessoal/${colaboradorId}/${mes}?ano=${ano}`, { method: 'PUT', body: JSON.stringify({ alocacoes }) });

// ─── Gestão Orçamentária · Custos Gerais por Projeto ───────────────────────

// Item comum = sempre 1 linha (1 id) com N faixas de vigência (mes_inicio/mes_fim) dentro dela
// — "adicionar período" nunca cria linha nova, editar/excluir é direto por id.
export const getItensCustosGeraisProjeto      = (ano, projetoId) => apiFetch(`/orcamento/custos-gerais-projeto/itens?ano=${ano}${projetoId ? `&projeto_id=${projetoId}` : ''}`);
export const getMaoDeObraCustosGeraisProjeto  = (ano, projetoId) => apiFetch(`/orcamento/custos-gerais-projeto/mao-de-obra?ano=${ano}${projetoId ? `&projeto_id=${projetoId}` : ''}`);
export const getItensRealizadoCustosGeraisProjeto = (ano, projetoId) => apiFetch(`/orcamento/custos-gerais-projeto/realizado-itens?ano=${ano}${projetoId ? `&projeto_id=${projetoId}` : ''}`);
export const postItemCustoGeralProjeto        = (dados) => apiFetch('/orcamento/custos-gerais-projeto/itens', { method: 'POST', body: JSON.stringify(dados) });
export const putItemCustoGeralProjeto         = (id, dados) => apiFetch(`/orcamento/custos-gerais-projeto/itens/${id}`, { method: 'PUT', body: JSON.stringify(dados) });
export const deleteItemCustoGeralProjeto      = (id) => apiFetch(`/orcamento/custos-gerais-projeto/itens/${id}`, { method: 'DELETE' });

// Ticket médio de Viagens travado por ANO × projeto (leitura geral, escrita só admin)
export const getTicketsViagemProjeto          = (projetoId, ano) => apiFetch(`/orcamento/custos-gerais-projeto/tickets-viagem?projeto_id=${projetoId}&ano=${ano}`);
export const putTicketViagemProjeto           = (projetoId, categoriaId, ticketMedio, ano) =>
  apiFetch(`/orcamento/custos-gerais-projeto/tickets-viagem?projeto_id=${projetoId}&ano=${ano}`, { method: 'PUT', body: JSON.stringify({ categoria_id: categoriaId, ticket_medio: ticketMedio }) });

// Lançamento de Viagem em grupo: sempre 4 linhas fixas (1 por subcategoria), cada uma com N
// faixas — "adicionar período" nunca cria linha nova, só mais uma faixa nas 4 linhas.
export const postGrupoViagemProjeto           = (dados) => apiFetch('/orcamento/custos-gerais-projeto/viagens', { method: 'POST', body: JSON.stringify(dados) });
export const putGrupoViagemProjeto            = (itemGrupoId, dados) => apiFetch(`/orcamento/custos-gerais-projeto/viagens/${itemGrupoId}`, { method: 'PUT', body: JSON.stringify(dados) });
export const deleteGrupoViagemProjeto         = (itemGrupoId) => apiFetch(`/orcamento/custos-gerais-projeto/viagens/${itemGrupoId}`, { method: 'DELETE' });

// "Lançar Kit Admissão" (2.7 Equipamentos e Materiais) — usa o ticket já cadastrado em
// Premissas · Equipamentos e Materiais (getEquipamentosTicketMedio), sem ticket próprio.
export const postGrupoKitAdmissaoProjeto      = (dados) => apiFetch('/orcamento/custos-gerais-projeto/kits-admissao', { method: 'POST', body: JSON.stringify(dados) });
export const putGrupoKitAdmissaoProjeto       = (itemGrupoId, dados) => apiFetch(`/orcamento/custos-gerais-projeto/kits-admissao/${itemGrupoId}`, { method: 'PUT', body: JSON.stringify(dados) });
export const deleteGrupoKitAdmissaoProjeto    = (itemGrupoId) => apiFetch(`/orcamento/custos-gerais-projeto/kits-admissao/${itemGrupoId}`, { method: 'DELETE' });

// ─── Gestão Orçamentária · Premissas · Equipamentos e Materiais ────────────
// Ticket médio travado por ANO, global, lista fixa de itens (leitura geral, escrita só admin).

export const getEquipamentosTicketMedio       = (ano) => apiFetch(`/orcamento/premissas/equipamentos?ano=${ano}`);
export const putEquipamentoTicketMedio        = (codigo, ticketMedio, ano) =>
  apiFetch(`/orcamento/premissas/equipamentos/${encodeURIComponent(codigo)}?ano=${ano}`, { method: 'PUT', body: JSON.stringify({ ticket_medio: ticketMedio }) });

// ─── Gestão Orçamentária · Despesas Financeiras ────────────────────────────

export const getCategoriasDespesasFinanceiras      = () => apiFetch('/orcamento/despesas-financeiras/categorias');
export const postCategoriaDespesaFinanceira        = (dados) => apiFetch('/orcamento/despesas-financeiras/categorias', { method: 'POST', body: JSON.stringify(dados) });
export const putCategoriaDespesaFinanceira         = (id, dados) => apiFetch(`/orcamento/despesas-financeiras/categorias/${id}`, { method: 'PUT', body: JSON.stringify(dados) });
export const deleteCategoriaDespesaFinanceira      = (id) => apiFetch(`/orcamento/despesas-financeiras/categorias/${id}`, { method: 'DELETE' });

export const getDespesasFinanceiras                = (ano) => apiFetch(`/orcamento/despesas-financeiras?ano=${ano}`);
export const postDespesaFinanceira                 = (dados) => apiFetch('/orcamento/despesas-financeiras', { method: 'POST', body: JSON.stringify(dados) });
export const putDespesaFinanceira                  = (id, dados) => apiFetch(`/orcamento/despesas-financeiras/${id}`, { method: 'PUT', body: JSON.stringify(dados) });
export const deleteDespesaFinanceira                = (id) => apiFetch(`/orcamento/despesas-financeiras/${id}`, { method: 'DELETE' });

// ─── Gestão Orçamentária · Impostos ─────────────────────────────────────────

export const getCategoriasImpostos                 = () => apiFetch('/orcamento/impostos/categorias');
export const postCategoriaImposto                  = (dados) => apiFetch('/orcamento/impostos/categorias', { method: 'POST', body: JSON.stringify(dados) });
export const putCategoriaImposto                   = (id, dados) => apiFetch(`/orcamento/impostos/categorias/${id}`, { method: 'PUT', body: JSON.stringify(dados) });
export const deleteCategoriaImposto                 = (id) => apiFetch(`/orcamento/impostos/categorias/${id}`, { method: 'DELETE' });

export const getImpostos                           = (ano) => apiFetch(`/orcamento/impostos?ano=${ano}`);
export const postImposto                           = (dados) => apiFetch('/orcamento/impostos', { method: 'POST', body: JSON.stringify(dados) });
export const putImposto                             = (id, dados) => apiFetch(`/orcamento/impostos/${id}`, { method: 'PUT', body: JSON.stringify(dados) });
export const deleteImposto                          = (id) => apiFetch(`/orcamento/impostos/${id}`, { method: 'DELETE' });

// ─── Gestão Orçamentária · Investimentos ────────────────────────────────────

export const getCategoriasInvestimentos            = () => apiFetch('/orcamento/investimentos/categorias');
export const postCategoriaInvestimento              = (dados) => apiFetch('/orcamento/investimentos/categorias', { method: 'POST', body: JSON.stringify(dados) });
export const putCategoriaInvestimento               = (id, dados) => apiFetch(`/orcamento/investimentos/categorias/${id}`, { method: 'PUT', body: JSON.stringify(dados) });
export const deleteCategoriaInvestimento            = (id) => apiFetch(`/orcamento/investimentos/categorias/${id}`, { method: 'DELETE' });

export const getInvestimentos                       = (ano) => apiFetch(`/orcamento/investimentos?ano=${ano}`);
export const postInvestimento                       = (dados) => apiFetch('/orcamento/investimentos', { method: 'POST', body: JSON.stringify(dados) });
export const putInvestimento                        = (id, dados) => apiFetch(`/orcamento/investimentos/${id}`, { method: 'PUT', body: JSON.stringify(dados) });
export const deleteInvestimento                     = (id) => apiFetch(`/orcamento/investimentos/${id}`, { method: 'DELETE' });

// ─── Gestão Orçamentária · DRE Gerencial (somente leitura) ──────────────────

export const getDreGerencial                        = (ano) => apiFetch(`/orcamento/dre-gerencial?ano=${ano}`);
export const getDreGerencialAcumulado                = (ano, mesInicio, mesFim) => apiFetch(`/orcamento/dre-gerencial/acumulado?ano=${ano}&mesInicio=${mesInicio}&mesFim=${mesFim}`);

// ─── Gestão Orçamentária · Análise por Departamento (somente leitura) ───────

export const getAnaliseDepartamento                  = (ano) => apiFetch(`/orcamento/analise-departamento?ano=${ano}`);

// ─── Gestão Orçamentária · Visão Executiva Custos / Visão Executiva Despesas (somente leitura) ───

export const getVisaoExecutivaGeral                  = (ano) => apiFetch(`/orcamento/visao-executiva-geral?ano=${ano}`);

// ─── Gestão Orçamentária · DRE por Empresa ──────────────────────────────────

export const getDreEmpresa                           = (ano) => apiFetch(`/orcamento/dre-empresa?ano=${ano}`);
export const getRateioEmpresas                       = (ano) => apiFetch(`/orcamento/rateio-empresas?ano=${ano}`);
export const putRateioEmpresa                        = (dados) => apiFetch('/orcamento/rateio-empresas', { method: 'PUT', body: JSON.stringify(dados) });
export const getPrejuizoFiscalSaldo                  = (ano) => apiFetch(`/orcamento/rateio-empresas/prejuizo-fiscal?ano=${ano}`);
export const putPrejuizoFiscalSaldo                  = (dados) => apiFetch('/orcamento/rateio-empresas/prejuizo-fiscal', { method: 'PUT', body: JSON.stringify(dados) });
export const getImpostosCalculado                    = (ano) => apiFetch(`/orcamento/impostos/calculado?ano=${ano}`);

// ─── Gestão de Estoque (Facilities) ─────────────────────────────────────────

export const getEstoqueCategorias       = (apenasAtivos = false) => apiFetch(`/estoque/categorias?apenas_ativos=${apenasAtivos}`);
export const postEstoqueCategoria       = (dados) => apiFetch('/estoque/categorias', { method: 'POST', body: JSON.stringify(dados) });
export const putEstoqueCategoria        = (id, dados) => apiFetch(`/estoque/categorias/${id}`, { method: 'PUT', body: JSON.stringify(dados) });
export const getEstoqueUnidades         = (apenasAtivos = false) => apiFetch(`/estoque/unidades?apenas_ativos=${apenasAtivos}`);
export const postEstoqueUnidade         = (dados) => apiFetch('/estoque/unidades', { method: 'POST', body: JSON.stringify(dados) });
export const putEstoqueUnidade          = (id, dados) => apiFetch(`/estoque/unidades/${id}`, { method: 'PUT', body: JSON.stringify(dados) });
export const getEstoqueProdutos         = (apenasAtivos = false) => apiFetch(`/estoque/produtos?apenas_ativos=${apenasAtivos}`);
export const postEstoqueProduto         = (dados) => apiFetch('/estoque/produtos', { method: 'POST', body: JSON.stringify(dados) });
export const putEstoqueProduto          = (id, dados) => apiFetch(`/estoque/produtos/${id}`, { method: 'PUT', body: JSON.stringify(dados) });
export const deleteEstoqueProduto       = (id) => apiFetch(`/estoque/produtos/${id}`, { method: 'DELETE' });
export const getEstoqueFornecedores     = (apenasAtivos = false) => apiFetch(`/estoque/fornecedores?apenas_ativos=${apenasAtivos}`);
export const postEstoqueFornecedor      = (dados) => apiFetch('/estoque/fornecedores', { method: 'POST', body: JSON.stringify(dados) });
export const putEstoqueFornecedor       = (id, dados) => apiFetch(`/estoque/fornecedores/${id}`, { method: 'PUT', body: JSON.stringify(dados) });
export const getEstoqueParametros       = () => apiFetch('/estoque/parametros');
export const putEstoqueParametro        = (chave, valor) => apiFetch(`/estoque/parametros/${chave}`, { method: 'PUT', body: JSON.stringify({ valor }) });
export const getEstoqueAbertura         = () => apiFetch('/estoque/abertura');
export const postEstoqueAbertura        = (dados) => apiFetch('/estoque/abertura', { method: 'POST', body: JSON.stringify(dados) });
export const putEstoqueContagem         = (inventarioId, produtoId, dados) => apiFetch(`/estoque/inventarios/${inventarioId}/itens/${produtoId}/contagem`, { method: 'PUT', body: JSON.stringify(dados) });
export const putEstoqueValorAbertura    = (inventarioId, produtoId, dados) => apiFetch(`/estoque/abertura/${inventarioId}/itens/${produtoId}/valor`, { method: 'PUT', body: JSON.stringify(dados) });
export const postEstoqueConcluirAbertura = (inventarioId) => apiFetch(`/estoque/abertura/${inventarioId}/concluir`, { method: 'POST' });
export const postEstoqueCancelarInventario = (inventarioId, motivo) => apiFetch(`/estoque/inventarios/${inventarioId}/cancelar`, { method: 'POST', body: JSON.stringify({ motivo }) });
export const getEstoquePosicao          = (data) => apiFetch(`/estoque/posicao${data ? `?data=${data}` : ''}`);
export const getEstoqueContexto         = () => apiFetch('/estoque/contexto');
const _qs = (params) => {
  const q = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== ''));
  return q.toString() ? `?${q}` : '';
};
export const getEstoqueNotasFiscais     = (params = {}) => apiFetch(`/estoque/notas-fiscais${_qs(params)}`);
export const getEstoqueNotaFiscal       = (id) => apiFetch(`/estoque/notas-fiscais/${id}`);
export const postEstoqueNotaFiscal      = (dados) => apiFetch('/estoque/notas-fiscais', { method: 'POST', body: JSON.stringify(dados) });
export const postEstoqueCancelarNotaFiscal = (id, dados) => apiFetch(`/estoque/notas-fiscais/${id}/cancelar`, { method: 'POST', body: JSON.stringify(dados) });
export const getEstoqueSugestaoFator    = (produtoId, fornecedorId) => apiFetch(`/estoque/notas-fiscais/sugestao-fator${_qs({ produto_id: produtoId, fornecedor_id: fornecedorId })}`);
export const postEstoqueAnexoNotaFiscal = (id, arquivo) => {
  const fd = new FormData();
  fd.append('arquivo', arquivo);
  return apiFetch(`/estoque/notas-fiscais/${id}/anexo`, { method: 'POST', body: fd });
};
// Anexo servido só por rota autenticada (não fica no /uploads público) — baixa como blob.
export async function baixarAnexoNotaFiscal(id, nomeArquivo) {
  const token = localStorage.getItem('token');
  const resp = await fetch(`${API_BASE_URL}/estoque/notas-fiscais/${id}/anexo`, { headers: { Authorization: `Bearer ${token}` } });
  if (!resp.ok) {
    const erro = await resp.json().catch(() => ({}));
    throw new Error(typeof erro.detail === 'string' ? erro.detail : `Erro ${resp.status}`);
  }
  const url = URL.createObjectURL(await resp.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = nomeArquivo || `nf_${id}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export const postEstoqueSaidas          = (dados) => apiFetch('/estoque/saidas', { method: 'POST', body: JSON.stringify(dados) });
export const postEstoqueAjuste          = (dados) => apiFetch('/estoque/ajustes', { method: 'POST', body: JSON.stringify(dados) });
export const postEstoqueCancelarMovimento = (id, dados) => apiFetch(`/estoque/movimentacoes/${id}/cancelar`, { method: 'POST', body: JSON.stringify(dados) });
export const getEstoqueMovimentacoes    = (params = {}) => apiFetch(`/estoque/movimentacoes${_qs(params)}`);
export const getEstoqueKardex           = (produtoId, params = {}) => apiFetch(`/estoque/produtos/${produtoId}/kardex${_qs(params)}`);
export const getEstoqueRelatorioMovimentacao = (params = {}) => apiFetch(`/estoque/relatorios/movimentacao${_qs(params)}`);
export const getEstoqueInventarios      = () => apiFetch('/estoque/inventarios');
export const getEstoqueInventario       = (id) => apiFetch(`/estoque/inventarios/${id}`);
export const postEstoqueInventario      = (dados) => apiFetch('/estoque/inventarios', { method: 'POST', body: JSON.stringify(dados) });
export const postEstoqueConcluirInventario = (id, dados) => apiFetch(`/estoque/inventarios/${id}/concluir`, { method: 'POST', body: JSON.stringify(dados) });
export const getEstoquePeriodos         = () => apiFetch('/estoque/periodos');
export const getEstoquePreviaFechamento = (ano, mes) => apiFetch(`/estoque/periodos/${ano}/${mes}`);
export const postEstoqueFecharPeriodo   = (ano, mes, dados) => apiFetch(`/estoque/periodos/${ano}/${mes}/fechar`, { method: 'POST', body: JSON.stringify(dados) });
export const postEstoqueReabrirPeriodo  = (ano, mes, motivo) => apiFetch(`/estoque/periodos/${ano}/${mes}/reabrir`, { method: 'POST', body: JSON.stringify({ motivo }) });
export const getEstoqueDashboard        = (ano, mes) => apiFetch(`/estoque/dashboard${_qs({ ano, mes })}`);
export const getEstoqueCustosProduto    = (produtoId) => apiFetch(`/estoque/produtos/${produtoId}/custos`);
export const getEstoqueInsights         = (ano, mes) => apiFetch(`/estoque/insights${_qs({ ano, mes })}`);
export const putEstoqueInsightStatus    = (dados) => apiFetch('/estoque/insights/status', { method: 'PUT', body: JSON.stringify(dados) });

// ─── Central de Ajuda ───────────────────────────────────────────────────────

export const postCentralAjudaFeedback        = (formData) => apiFetch('/central-ajuda/feedback', { method: 'POST', body: formData });
export const getMeusFeedbacksCentralAjuda     = () => apiFetch('/central-ajuda/feedback/meus');
export const getAdminFeedbacksCentralAjuda    = (queryString = '') => apiFetch(`/central-ajuda/feedback${queryString ? `?${queryString}` : ''}`);
export const patchAdminFeedbackCentralAjuda   = (id, dados) => apiFetch(`/central-ajuda/feedback/${id}`, { method: 'PATCH', body: JSON.stringify(dados) });

export function getUsuarioLogado() {
  const token = localStorage.getItem('token');
  if (!token) return null;
  try {
    return jwtDecode(token);
  } catch {
    return null;
  }
}